/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField } from "@mui/material";
import useOrganization from "hooks/useOrganization";
import { ListItemContext } from "cms/fields/field-lists/field-list-context";
import { SettingsStyleEdit } from "cms/fields/layout/controls/settings-style";
import { DisplaySettingsWrapper } from "cms/fields/layout/display-settings-wrapper";
import { useSelectFieldSchema } from "cms/fields/hooks/use-select-field-schema";
import { isArrayNotEmpty, isStringNotEmpty } from "utils/empty-exists";
import debounce from "lodash.debounce";
// import useThrottle from 'hooks/useThrottle'
import "styles/react-datepicker.css";

const TimezoneFieldItemEdit = () => {
    
    const { t } = useTranslation();
    // const throttler = useThrottle()
    
    const {
        type,
        name,
        index,
        value,
        settings,
        updateListItem,
        disabled = false
    } = useContext(ListItemContext);

    const schema = useSelectFieldSchema(name);
    const { settings: schemaSettings } = schema || {};
    const { styleOptions, placeholder } = schemaSettings || {};
    
    const ph = placeholder || t("commons.placeholderText");
    const organization = useOrganization() || {};
    const defaultTimezone = organization.tz;
    let zoneList = Intl.supportedValuesOf("timeZone");
    const enumOptions = [];
    isArrayNotEmpty(zoneList) &&
        zoneList.map((item) =>
            enumOptions.push({
                label: item,
                value: item,
            }),
        );
    const tz = isStringNotEmpty(value) ? value : defaultTimezone;
    const initialState = {
        value: tz,
        settings
    };
    const [itemLocalState, setItemLocalState] = useState(initialState);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const update = useCallback(
        debounce((updateState, index) => {
            updateListItem(updateState, index);
        }, 1200),
        [updateListItem]
    );
    const onSettingsChange = (updateState) => {
        setItemLocalState(updateState);
        update(updateState, index);
    };
    const onChange = (e, value) => {
        const newValue = value?.value;
        const newState = { ...itemLocalState, value: newValue };
        setItemLocalState(newState);
        update(newState, index);
    };
    
    return (
        <Fragment>
            <div className='simplicity-composer-field-primary-layout'>
                <div className='simplicity-composer-field-primary'>
                    {isArrayNotEmpty(enumOptions) &&
                        <Autocomplete
                            id={`${name}_${type}_enum`}
                            // disableCloseOnSelect
                            options={enumOptions}
                            placeholder={ph}
                            onChange={onChange}
                            disabled={disabled}
                            disableClearable={true}
                            value={enumOptions.find((item) => item.label === itemLocalState?.value)}
                            renderInput={(params) => <TextField variant='standard' {...params} />}
                        />
                    }
                </div>
                <DisplaySettingsWrapper>
                    {/*  Reads from schema options.styleOptions */}
                    {/*  records selection as value settings.style */}
                    <SettingsStyleEdit
                        type={type}
                        name={name}
                        label={t("commons.displayStyle")}
                        itemLocalState={itemLocalState}
                        setItemLocalState={setItemLocalState}
                        disabled={disabled}
                        styleOptions={styleOptions}
                        selected={itemLocalState?.settings?.style}
                        index={index}
                        update={onSettingsChange}
                    />
                </DisplaySettingsWrapper>
            </div>
        </Fragment>
    );
};
TimezoneFieldItemEdit.displayName = "TimezoneFieldItemEdit";
export {
    TimezoneFieldItemEdit
};
